import { useMemo, useState } from "react";
import { useLive, post } from "../lib/api";
import type { PropertyRow, PropertyStatusInfo } from "../lib/api";
import { Card, Pill, Button, Empty, Carregando } from "../lib/ui";
import { Fotos } from "../lib/Fotos";

/**
 * Catálogo, agrupado por empreendimento.
 *
 * Pré-lançamento tem centenas de unidades do mesmo prédio. Uma lista plana
 * ficaria ilegível e, pior, faria o operador desistir de manter o status em
 * dia — e catálogo desatualizado é a IA oferecendo o que já foi vendido.
 */
export function Imoveis() {
  const { data, reload } = useLive<{
    imoveis: PropertyRow[];
    storage: { arquivos: number; bytes: number };
    catalogo: "on" | "off";
  }>("/properties", ["properties"]);
  const { data: STATUS } = useLive<PropertyStatusInfo[]>(
    "/properties/status",
    [],
  );
  const [busca, setBusca] = useState("");
  const [soDisponivel, setSoDisponivel] = useState(false);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());

  const grupos = useMemo(() => {
    if (!data) return [];
    const filtrados = data.imoveis.filter((p) => {
      if (soDisponivel && p.status !== "disponivel") return false;
      if (!busca.trim()) return true;
      const t = busca.toLowerCase();
      return [p.title, p.external_id, p.development, p.unit, p.neighborhood]
        .filter(Boolean)
        .some((c) => c!.toLowerCase().includes(t));
    });

    const mapa = new Map<string, PropertyRow[]>();
    for (const p of filtrados) {
      const chave = p.development ?? "__avulsos__";
      const lista = mapa.get(chave) ?? [];
      lista.push(p);
      mapa.set(chave, lista);
    }
    return [...mapa.entries()];
  }, [data, busca, soDisponivel]);

  if (!data || !STATUS) return <Carregando />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="buscar por unidade, empreendimento, bairro…"
          className="flex-1 min-w-48 bg-surface-2 border border-rule-2 rounded-lg
            px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent outline-none"
        />
        <label className="flex items-center gap-1.5 text-xs text-ink-3 cursor-pointer">
          <input
            type="checkbox"
            checked={soDisponivel}
            onChange={(e) => setSoDisponivel(e.target.checked)}
          />
          só disponíveis
        </label>
        <span className="text-xs text-ink-3 tabular-nums">
          {data.imoveis.length} unidades · {mb(data.storage.bytes)}
        </span>
      </div>

      {data.catalogo === "off" && (
        <Card stripe="accent" className="p-3">
          <p className="text-xs text-ink-2 leading-relaxed max-w-prose">
            <span className="text-accent">Catálogo desligado.</span> A IA não
            oferece unidade específica — ela fala do empreendimento pelo que
            está em{" "}
            <span className="text-ink">O que a IA sabe</span>, qualifica o lead
            e passa pro corretor quando a conversa chega em apartamento, preço
            ou disponibilidade.
          </p>
          <p className="text-xs text-ink-3 mt-1.5 max-w-prose">
            As unidades abaixo estão suspensas de propósito, não é erro. Para
            religar: <code className="text-ink-2">AXIS_CATALOGO=on</code> no{" "}
            <code className="text-ink-2">.env</code>, e marque as unidades como
            disponíveis. Antes de religar, preencha quartos e bairro — sem
            eles a ficha vai incompleta pro cliente.
          </p>
        </Card>
      )}

      <p className="text-xs text-ink-3 max-w-prose">
        A ficha vem da planilha —{" "}
        <code className="text-ink-2">pnpm catalogo tabela.xlsx</code>. Aqui você
        muda status e sobe fotos. Importar <span className="text-ink">não</span>{" "}
        mexe em unidade de outro empreendimento.
      </p>

      {grupos.length === 0 && <Empty>Nada encontrado.</Empty>}

      {grupos.map(([nome, unidades]) => (
        <Grupo
          key={nome}
          nome={nome}
          unidades={unidades}
          status={STATUS}
          aberto={abertos.has(nome)}
          onToggle={() =>
            setAbertos((s) => {
              const n = new Set(s);
              n.has(nome) ? n.delete(nome) : n.add(nome);
              return n;
            })
          }
          onChange={reload}
        />
      ))}
    </div>
  );
}

function Grupo({
  nome,
  unidades,
  status,
  aberto,
  onToggle,
  onChange,
}: {
  nome: string;
  unidades: PropertyRow[];
  status: PropertyStatusInfo[];
  aberto: boolean;
  onToggle: () => void;
  onChange: () => void;
}) {
  const avulsos = nome === "__avulsos__";
  const disponiveis = unidades.filter((u) => u.status === "disponivel").length;

  return (
    <Card className="overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-surface-2 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ink-3 text-xs">{aberto ? "▾" : "▸"}</span>
          <span className="text-ink font-medium">
            {avulsos ? "Imóveis avulsos" : nome}
          </span>
          <span className="text-xs text-ink-3">
            {unidades.length} unidade{unidades.length > 1 ? "s" : ""}
          </span>
          {disponiveis === 0 ? (
            <Pill tone="danger">nenhuma disponível</Pill>
          ) : (
            <Pill tone="ok">{disponiveis} disponível(is)</Pill>
          )}
          {unidades.some((u) => u.fotos === 0) && (
            <Pill tone="warm">
              {unidades.filter((u) => u.fotos === 0).length} sem foto
            </Pill>
          )}
        </div>
      </button>

      {aberto && (
        <div className="border-t border-rule">
          {!avulsos && (
            <div className="px-3 py-2 bg-surface-2/50 border-b border-rule flex flex-wrap items-center gap-2">
              <span className="text-xs text-ink-3">
                Mudar o empreendimento inteiro:
              </span>
              {status.map((s) => (
                <Button
                  key={s.status}
                  variant={s.status === "disponivel" ? "default" : "ghost"}
                  onClick={async () => {
                    if (
                      !confirm(
                        `Marcar as ${unidades.length} unidades de "${nome}" como ${s.rotulo.toLowerCase()}?`,
                      )
                    )
                      return;
                    await post("/developments/status", {
                      development: nome,
                      status: s.status,
                    });
                    onChange();
                  }}
                >
                  {s.rotulo}
                </Button>
              ))}
            </div>
          )}

          {unidades.map((p) => (
            <Unidade
              key={p.id}
              p={p}
              status={status}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function Unidade({
  p,
  status,
  onChange,
}: {
  p: PropertyRow;
  status: PropertyStatusInfo[];
  onChange: () => void;
}) {
  const [fotosAbertas, setFotosAbertas] = useState(false);

  return (
    <div
      className={`px-3 py-2 border-b border-rule last:border-b-0 ${
        p.status !== "disponivel" ? "opacity-55" : ""
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink">
          {p.unit ? `Unidade ${p.unit}` : p.title}
        </span>
        <span className="font-mono text-xs text-ink-3">{p.external_id}</span>

        <span className="text-xs text-ink-3">
          {[
            p.bedrooms ? `${p.bedrooms}q` : null,
            p.parking ? `${p.parking} vaga` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>

        <button
          onClick={() => setFotosAbertas((v) => !v)}
          className={`text-xs px-1.5 py-0.5 rounded-lg border transition-colors ${
            p.fotos === 0
              ? "border-warm/40 text-warm hover:bg-warm-dim"
              : "border-rule-2 text-ink-3 hover:text-ink"
          }`}
        >
          {p.fotos === 0 ? "sem foto" : `${p.fotos} foto(s)`}
        </button>

        <span className="ml-auto font-mono text-xs tabular-nums text-ink-2">
          {p.price
            ? Number(p.price).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
                maximumFractionDigits: 0,
              })
            : "sob consulta"}
        </span>

        {/* Status como select: com 200 unidades, quatro botões por linha
            viraria uma parede de botões. */}
        <select
          value={p.status}
          onChange={async (e) => {
            await post(`/properties/${p.id}/status`, { status: e.target.value });
            onChange();
          }}
          title={status.find((s) => s.status === p.status)?.ajuda}
          className={`bg-surface-2 border rounded-lg px-1.5 py-0.5 text-xs outline-none
            focus:border-accent ${
              p.status === "disponivel"
                ? "border-ok/40 text-ok"
                : "border-rule-2 text-ink-3"
            }`}
        >
          {status.map((s) => (
            <option key={s.status} value={s.status}>
              {s.rotulo}
            </option>
          ))}
        </select>
      </div>

      {fotosAbertas && (
        <div className="mt-2 pt-2 border-t border-rule">
          <Fotos base={`/api/properties/${p.id}`} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

function mb(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
